/**
 * Data layer for `api-key-header-auth`. Thin typed fetch wrappers over /api/v1/api-key-header-auth.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ApiKeyHeaderAuthRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListApiKeyHeaderAuthResponse {
	count: number;
	items: ApiKeyHeaderAuthRow[];
}

export interface CreateApiKeyHeaderAuthBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/api-key-header-auth";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListApiKeyHeaderAuthResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListApiKeyHeaderAuthResponse>(res);
}

export async function create(body: CreateApiKeyHeaderAuthBody): Promise<ApiKeyHeaderAuthRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<ApiKeyHeaderAuthRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export interface TestRequestResult {
	status: number;
	ok: boolean;
	body: string;
}

/**
 * Perform a test GET against `/keys` with a user-supplied header + secret.
 * Returns `{status, ok, body}` regardless of HTTP status so the UI can render
 * the server's reply. Only network errors reject.
 */
export async function testRequest(
	headerName: string,
	secret: string,
): Promise<TestRequestResult> {
	const res = await fetch(`${BASE}/keys`, {
		method: "GET",
		credentials: "include",
		headers: {
			accept: "application/json",
			[headerName]: secret,
		},
	});
	const body = await res.text();
	return { status: res.status, ok: res.ok, body };
}

export const apiKeyHeaderAuthQueryKeys = {
	all: ["api-key-header-auth"] as const,
	list: () => [...apiKeyHeaderAuthQueryKeys.all] as const,
	detail: (id: string) => [...apiKeyHeaderAuthQueryKeys.all, id] as const,
};
